import dayjs from "dayjs"

import { type PortfolioMockStage } from "./usePortfolioDbStorage"

/** Color/weight for due-date Text rendering: red when overdue/within a week,
 *  amber when within two weeks, undefined otherwise (default styling). */
export const getDueDateStyle = (dueDate?: string) => {
  if (!dueDate) return undefined
  const due = dayjs(dueDate)
  if (!due.isValid()) return undefined

  const daysLeft = due.startOf("day").diff(dayjs().startOf("day"), "day")
  if (daysLeft <= 7) return { color: "#cf1322", fontWeight: 600 }
  if (daysLeft <= 14) return { color: "#d48806", fontWeight: 600 }
  return undefined
}

/** Heuristic priority used by `selectVisibleTimelineStages` to decide which
 *  N-stage window to show on the project list row. Higher = more relevant. */
const getStagePriorityScore = (stage: PortfolioMockStage): number => {
  if (stage.status === "complete") return 0
  const due = dayjs(stage.dueDate)
  if (!due.isValid()) return 10

  const daysUntilDue = due.startOf("day").diff(dayjs().startOf("day"), "day")
  if (daysUntilDue < 0) return 200 + Math.max(-daysUntilDue, 0)
  if (daysUntilDue <= 7) return 160
  if (daysUntilDue <= 14) return 120
  return 60
}

/** Picks the most relevant contiguous window of stages to show on a project
 *  row. Falls back to the tail when nothing is "relevant" (everything done /
 *  no due dates). */
export const selectVisibleTimelineStages = (
  orderedStages: PortfolioMockStage[],
  maxVisible: number
): PortfolioMockStage[] => {
  if (orderedStages.length <= maxVisible) return orderedStages

  const hasRelevantStages = orderedStages.some(
    (stage) => getStagePriorityScore(stage) > 0
  )
  if (!hasRelevantStages) {
    return orderedStages.slice(-maxVisible)
  }

  let bestStart = 0
  let bestScore = Number.NEGATIVE_INFINITY
  let bestIncompleteCount = -1

  for (let start = 0; start <= orderedStages.length - maxVisible; start += 1) {
    const segment = orderedStages.slice(start, start + maxVisible)
    const score = segment.reduce(
      (acc, stage) => acc + getStagePriorityScore(stage),
      0
    )
    const incompleteCount = segment.filter(
      (stage) => stage.status !== "complete"
    ).length

    const isBetter =
      score > bestScore ||
      (score === bestScore && incompleteCount > bestIncompleteCount) ||
      (score === bestScore &&
        incompleteCount === bestIncompleteCount &&
        start > bestStart)

    if (isBetter) {
      bestStart = start
      bestScore = score
      bestIncompleteCount = incompleteCount
    }
  }

  return orderedStages.slice(bestStart, bestStart + maxVisible)
}
